import { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

function useWindowSize() {
  const [size, setSize] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1200 });
  useEffect(() => {
    const h = () => setSize({ w: window.innerWidth });
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return size;
}

function fmtNum(n) {
  if (n == null) return 'N/A';
  const neg = n < 0, a = Math.abs(n);
  const s = neg ? '-' : '';
  if (a >= 1e12) return s + (a/1e12).toFixed(2) + 'T';
  if (a >= 1e9)  return s + (a/1e9).toFixed(2) + 'B';
  if (a >= 1e6)  return s + (a/1e6).toFixed(2) + 'M';
  if (a >= 1e3)  return s + (a/1e3).toFixed(2) + 'K';
  return s + a.toFixed(2);
}

function calcHealth(ov) {
  if (!ov) return 0;
  let s = 50;
  if (ov.profitMargin > 0.2) s += 10; else if (ov.profitMargin > 0.1) s += 5;
  if (ov.returnOnEquity > 0.15) s += 10; else if (ov.returnOnEquity > 0.1) s += 5;
  if (ov.peRatio && ov.peRatio < 25 && ov.peRatio > 10) s += 10;
  if (ov.pegRatio && ov.pegRatio < 2) s += 5;
  if (ov.eps > 0) s += 10;
  if (ov.revenuePerShare > 0) s += 5;
  return Math.min(100, Math.max(0, s));
}



function formatNewsDate(dateStr) {
  if (!dateStr) return '';
  try {
    // Alpha Vantage format: YYYYMMDDTHHMMSS
    if (/^\d{8}T/.test(dateStr)) {
      const y = dateStr.substring(0,4), m = dateStr.substring(4,6), d = dateStr.substring(6,8);
      const date = new Date(`${y}-${m}-${d}`);
      const diff = Math.ceil((new Date() - date) / 86400000);
      if (diff <= 0) return 'Today';
      if (diff === 1) return 'Yesterday';
      if (diff < 7) return `${diff} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // ISO / other format
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch(e) { return dateStr; }
}


// ═══════════════════════════════════════════════════════════════════
// ALPHA INTELLIGENCE™ — matches CompanyAnalysis exactly
// Calls /news/sentiment/:symbol — aggregate + articles
// ═══════════════════════════════════════════════════════════════════
function AlphaIntelligencePanel({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState('week');

  const fetchData = () => {
    setLoading(true); setError(null);
    fetch(`${API_URL}/news/sentiment/${symbol}?timeframe=${timeframe}`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { if (symbol) fetchData(); }, [symbol, timeframe]);

  const sentColor = (label) => {
    if (!label) return '#94a3b8';
    const l = label.toLowerCase();
    if (l.includes('bull') || l.includes('pos')) return '#10b981';
    if (l.includes('bear') || l.includes('neg')) return '#ef4444';
    return '#f59e0b';
  };

  const fmtAIDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      if (/^\d{8}T/.test(dateStr)) {
        const y=dateStr.substring(0,4),m=dateStr.substring(4,6),d=dateStr.substring(6,8);
        const date = new Date(`${y}-${m}-${d}`);
        const diff = Math.ceil((new Date()-date)/86400000);
        if (diff<=0) return 'Today'; if (diff===1) return 'Yesterday';
        if (diff<7) return `${diff}d ago`;
        return date.toLocaleDateString('en-US',{month:'short',day:'numeric'});
      }
      return new Date(dateStr).toLocaleDateString('en-US',{month:'short',day:'numeric'});
    } catch(e){ return dateStr; }
  };

  if (loading) return (
    <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'0.75rem',padding:'2rem',textAlign:'center'}}>
      <div style={{fontSize:28,animation:'spin 1s linear infinite',display:'inline-block'}}>⟳</div>
      <p style={{color:'#94a3b8',marginTop:'0.75rem',fontSize:'13px'}}>Loading Alpha Intelligence™...</p>
    </div>
  );

  if (error || !data) return (
    <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'0.75rem',padding:'1.5rem'}}>
      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
        <span style={{fontSize:20}}>✨</span>
        <span style={{fontSize:'14px',fontWeight:700,color:'#f1f5f9'}}>Alpha Intelligence™</span>
      </div>
      <p style={{color:'#64748b',fontSize:'12px',margin:'0 0 12px'}}>News sentiment data unavailable for {symbol}</p>
      <button onClick={fetchData} style={{padding:'6px 14px',borderRadius:'6px',background:'#334155',border:'1px solid #475569',color:'#cbd5e1',cursor:'pointer',fontSize:'11px',fontWeight:600}}>Retry</button>
    </div>
  );

  const { aggregate, articles=[], sentimentShift, specialNews={}, totalArticles=0 } = data;
  const agg = aggregate || {};
  const sc = sentColor(agg.overall);

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.25rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
          <span style={{fontSize:20,filter:'drop-shadow(0 0 8px #60a5fa)'}}>✨</span>
          <h3 style={{margin:0,fontSize:'1.125rem',fontWeight:700,color:'#f1f5f9'}}>Alpha Intelligence™</h3>
          <span style={{fontSize:'10px',background:'rgba(96,165,250,0.15)',border:'1px solid rgba(96,165,250,0.3)',color:'#60a5fa',padding:'2px 8px',borderRadius:'12px',fontWeight:700}}>LIVE</span>
        </div>
        <div style={{display:'flex',gap:'5px',flexWrap:'wrap'}}>
          {['today','week','month','all'].map(tf=>(
            <button key={tf} onClick={()=>setTimeframe(tf)} style={{
              padding:'5px 10px',borderRadius:'6px',cursor:'pointer',textTransform:'capitalize',
              border:timeframe===tf?'1px solid #60a5fa':'1px solid #334155',
              background:timeframe===tf?'rgba(96,165,250,0.2)':'rgba(30,41,59,0.5)',
              color:timeframe===tf?'#60a5fa':'#64748b',
              fontSize:'10px',fontWeight:700,transition:'all 0.15s',
            }}>{tf}</button>
          ))}
          <button onClick={fetchData} style={{padding:'5px 10px',borderRadius:'6px',border:'1px solid #334155',background:'rgba(30,41,59,0.5)',color:'#64748b',cursor:'pointer',fontSize:'12px'}}>↻</button>
        </div>
      </div>

      {/* Aggregate sentiment card */}
      {agg.overall && (
        <div style={{background:`linear-gradient(135deg,${sc}15,transparent)`,border:`1px solid ${sc}40`,borderRadius:'0.75rem',padding:'1.25rem',marginBottom:'1.25rem'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'1rem',marginBottom:'0.75rem'}}>
            <div>
              <div style={{fontSize:'1.375rem',fontWeight:800,color:sc,marginBottom:'3px'}}>{agg.overall}</div>
              <div style={{fontSize:'11px',color:'#64748b'}}>{totalArticles} articles analyzed · {timeframe} view</div>
            </div>
            <div style={{display:'flex',gap:'1.25rem'}}>
              {[{l:'Bullish',v:agg.bullish,c:'#10b981'},{l:'Neutral',v:agg.neutral,c:'#f59e0b'},{l:'Bearish',v:agg.bearish,c:'#ef4444'}].map(({l,v,c})=>(
                <div key={l} style={{textAlign:'center'}}>
                  <div style={{fontSize:'1.125rem',fontWeight:800,color:c}}>{v||0}</div>
                  <div style={{fontSize:'10px',color:'#64748b'}}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Sentiment bar */}
          <div style={{height:'6px',background:'#1e293b',borderRadius:'3px',overflow:'hidden',display:'flex'}}>
            <div style={{width:`${agg.bullishPct||0}%`,background:'#10b981',transition:'width 0.5s'}}/>
            <div style={{width:`${agg.neutralPct||0}%`,background:'#f59e0b',transition:'width 0.5s'}}/>
            <div style={{width:`${agg.bearishPct||0}%`,background:'#ef4444',transition:'width 0.5s'}}/>
          </div>
        </div>
      )}

      {/* Sentiment shift alert */}
      {sentimentShift?.detected && (
        <div style={{background:'rgba(251,191,36,0.08)',border:'1px solid rgba(251,191,36,0.3)',borderRadius:'0.75rem',padding:'0.875rem',marginBottom:'1.25rem',display:'flex',alignItems:'center',gap:'0.75rem'}}>
          <span style={{fontSize:18}}>⚠️</span>
          <div>
            <div style={{fontSize:'12px',fontWeight:700,color:'#fbbf24',marginBottom:'2px'}}>Sentiment Shift Detected</div>
            <div style={{fontSize:'11px',color:'#cbd5e1'}}>Shifted from <strong>{sentimentShift.from}</strong> → <strong>{sentimentShift.to}</strong> ({sentimentShift.direction})</div>
          </div>
        </div>
      )}

      {/* Special news */}
      {((specialNews.insiderActivity?.length||0)+(specialNews.mergers?.length||0))>0&&(
        <div style={{display:'flex',gap:'10px',marginBottom:'1.25rem',flexWrap:'wrap'}}>
          {specialNews.insiderActivity?.length>0&&(
            <div style={{background:'rgba(139,92,246,0.1)',border:'1px solid rgba(139,92,246,0.3)',borderRadius:'8px',padding:'10px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
              <span>👥</span><span style={{fontSize:'12px',fontWeight:700,color:'#a78bfa'}}>Insider Activity · {specialNews.insiderActivity.length}</span>
            </div>
          )}
          {specialNews.mergers?.length>0&&(
            <div style={{background:'rgba(236,72,153,0.1)',border:'1px solid rgba(236,72,153,0.3)',borderRadius:'8px',padding:'10px 14px',display:'flex',alignItems:'center',gap:'8px'}}>
              <span>🤝</span><span style={{fontSize:'12px',fontWeight:700,color:'#ec4899'}}>M&A Activity · {specialNews.mergers.length}</span>
            </div>
          )}
        </div>
      )}

      {/* Articles */}
      <div style={{display:'grid',gap:'10px'}}>
        {articles.map((a,i)=>{
          const sl = a.sentiment?.label||a.overall_sentiment_label||'';
          const sc2 = sentColor(sl);
          return (
            <div key={i} style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'10px',padding:'1rem',cursor:'pointer',transition:'all 0.2s'}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=sc2;e.currentTarget.style.transform='translateY(-2px)';}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='#334155';e.currentTarget.style.transform='none';}}
              onClick={()=>window.open(a.url,'_blank')}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'10px',marginBottom:'8px'}}>
                <h4 style={{margin:0,fontSize:'13px',fontWeight:600,color:'#f1f5f9',lineHeight:'1.4',flex:1}}>{a.title}</h4>
                {sl&&<span style={{fontSize:'10px',fontWeight:700,padding:'3px 8px',borderRadius:'5px',whiteSpace:'nowrap',flexShrink:0,
                  color:sc2,background:`${sc2}18`,border:`1px solid ${sc2}40`}}>{sl}</span>}
              </div>
              {(a.summary||a.banner_image)&&(
                <p style={{margin:'0 0 8px',fontSize:'12px',color:'#94a3b8',lineHeight:'1.5',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>
                  {a.summary}
                </p>
              )}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:'11px',color:'#475569',flexWrap:'wrap',gap:'6px'}}>
                <div style={{display:'flex',gap:'12px',alignItems:'center'}}>
                  <span style={{color:'#60a5fa',fontWeight:600}}>{a.source}</span>
                  {a.tickerSentiment?.relevance&&<span>Relevance: {Math.round(a.tickerSentiment.relevance*100)}%</span>}
                </div>
                <span>{fmtAIDate(a.publishedAt||a.date||'')}</span>
              </div>
              {a.impactScore>70&&(
                <div style={{marginTop:'8px',padding:'5px 8px',background:'rgba(251,191,36,0.08)',borderRadius:'5px',display:'flex',alignItems:'center',gap:'5px'}}>
                  <span style={{fontSize:12}}>⚡</span>
                  <span style={{fontSize:'10px',color:'#fbbf24',fontWeight:700}}>High Impact (Score: {a.impactScore})</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PRICE CHART — identical to CompanyAnalysis EnhancedPriceChart
// ═══════════════════════════════════════════════════════════════════
function SportsPriceChart({ data, symbol, timeframe }) {
  const [hov, setHov] = useState(null);
  const ref = useRef(null);
  if (!data || !data.length) return <div style={{padding:'3rem',textAlign:'center',color:'#64748b'}}>No price data available for {symbol}</div>;

  const prices = data.map(d => d.close), vols = data.map(d => d.volume||0);
  const minP = Math.min(...prices), maxP = Math.max(...prices);
  const maxV = Math.max(...vols)||1, avg = prices.reduce((a,b)=>a+b,0)/prices.length;
  const pad = (maxP-minP)*0.1||1;
  const first=data[0]?.close||0, last=data[data.length-1]?.close||0;
  const up=last>=first, tc=up?'#10b981':'#ef4444';
  const W=900,H=380,pl=65,pr=65,pt=20,pb=70;
  const cw=W-pl-pr, ch=H-pt-pb-40;
  const xs=i=>(i/(data.length-1))*cw;
  const ys=p=>ch-((p-(minP-pad))/(maxP-minP+2*pad))*ch;
  const vs=v=>(v/maxV)*35;
  const line=data.map((d,i)=>`${i===0?'M':'L'} ${xs(i)},${ys(d.close)}`).join(' ');
  const area=`${line} L ${xs(data.length-1)},${ch} L 0,${ch} Z`;
  const ma20=data.map((_,i)=>i<19?null:data.slice(i-19,i+1).reduce((a,b)=>a+b.close,0)/20);
  let maPath=''; let started=false;
  ma20.forEach((v,i)=>{ if(v!==null){ maPath+=`${started?'L':'M'} ${xs(i)},${ys(v)} `; started=true; }});

  const onMove=e=>{
    if(!ref.current) return;
    const r=ref.current.getBoundingClientRect();
    const x=(e.clientX-r.left-pl)/cw;
    const idx=Math.round(x*(data.length-1));
    if(idx>=0&&idx<data.length) setHov(idx);
  };
  const fmtD=d=>{
    const dt=new Date(d);
    return timeframe==='monthly'
      ? dt.toLocaleDateString('en-US',{month:'short',year:'2-digit'})
      : dt.toLocaleDateString('en-US',{month:'short',day:'numeric'});
  };
  const pctChg=((last-first)/first*100);

  return (
    <div>
      {/* Performance summary */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:'0.75rem',marginBottom:'1rem'}}>
        {[
          {l:'Period Change',v:`${pctChg>=0?'+':''}${pctChg.toFixed(2)}%`,c:tc},
          {l:'Period High',v:`$${maxP.toFixed(2)}`,c:'#10b981'},
          {l:'Period Low',v:`$${minP.toFixed(2)}`,c:'#ef4444'},
          {l:'Average Price',v:`$${avg.toFixed(2)}`,c:'#8b5cf6'},
        ].map(({l,v,c})=>(
          <div key={l} style={{background:'rgba(30,41,59,0.6)',border:`1px solid ${c}33`,borderRadius:'0.75rem',padding:'0.875rem'}}>
            <div style={{fontSize:'0.65rem',color:'#94a3b8',textTransform:'uppercase',fontWeight:700,marginBottom:'0.4rem'}}>{l}</div>
            <div style={{fontSize:'1.25rem',fontWeight:900,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Hover tooltip */}
      {hov!==null&&data[hov]&&(
        <div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap',marginBottom:'0.75rem',background:'rgba(15,23,42,0.95)',border:'1px solid #475569',borderRadius:'0.75rem',padding:'0.75rem 1.25rem'}}>
          {[
            {l:'Date',v:new Date(data[hov].date).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}),c:'#e2e8f0'},
            {l:'Open',v:`$${data[hov].open?.toFixed(2)||'N/A'}`,c:'#94a3b8'},
            {l:'High',v:`$${data[hov].high?.toFixed(2)||'N/A'}`,c:'#10b981'},
            {l:'Low',v:`$${data[hov].low?.toFixed(2)||'N/A'}`,c:'#ef4444'},
            {l:'Close',v:`$${data[hov].close?.toFixed(2)}`,c:tc},
            {l:'Volume',v:fmtNum(data[hov].volume||0),c:'#a78bfa'},
          ].map(({l,v,c})=>(
            <div key={l}><div style={{fontSize:'0.6rem',color:'#64748b',textTransform:'uppercase'}}>{l}</div><div style={{fontSize:'0.85rem',fontWeight:700,color:c}}>{v}</div></div>
          ))}
        </div>
      )}

      {/* Main SVG chart */}
      <div style={{background:'linear-gradient(180deg,rgba(30,41,59,0.8),rgba(15,23,42,0.9))',border:'1px solid #334155',borderRadius:'1rem',padding:'1rem',position:'relative'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
          <div>
            <div style={{fontSize:'1rem',fontWeight:700,color:'#e2e8f0'}}>📈 {symbol} — {timeframe.charAt(0).toUpperCase()+timeframe.slice(1)} Chart</div>
            <div style={{fontSize:'0.7rem',color:'#64748b'}}>{data.length} data points · Last: {fmtD(data[data.length-1]?.date)}</div>
          </div>
          <div style={{background:`${tc}20`,border:`1px solid ${tc}40`,borderRadius:'0.5rem',padding:'0.4rem 0.75rem',textAlign:'right'}}>
            <div style={{fontSize:'0.6rem',color:'#94a3b8',textTransform:'uppercase'}}>Current</div>
            <div style={{fontSize:'1.1rem',fontWeight:900,color:tc}}>${last.toFixed(2)}</div>
          </div>
        </div>
        <svg ref={ref} width="100%" viewBox={`0 0 ${W} ${H}`} style={{display:'block',maxWidth:'100%',cursor:'crosshair'}}
          onMouseMove={onMove} onMouseLeave={()=>setHov(null)}>
          <defs>
            <linearGradient id={`ag-${symbol}`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={tc} stopOpacity="0.35"/>
              <stop offset="100%" stopColor={tc} stopOpacity="0.02"/>
            </linearGradient>
            <filter id="gl2"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
          </defs>
          <g transform={`translate(${pl},${pt})`}>
            {[0,.25,.5,.75,1].map(r=>{
              const y=ch*r, p=maxP+pad-(maxP-minP+2*pad)*r;
              return <g key={r}><line x1={0} y1={y} x2={cw} y2={y} stroke="#334155" strokeWidth="1" strokeDasharray="4,4" opacity="0.5"/><text x={-8} y={y+4} textAnchor="end" fill="#64748b" fontSize="11">${p.toFixed(0)}</text></g>;
            })}
            <line x1={0} y1={ys(avg)} x2={cw} y2={ys(avg)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="8,4" opacity="0.6"/>
            <text x={cw+5} y={ys(avg)+4} fill="#f59e0b" fontSize="10">AVG</text>
            {data.map((d,i)=><rect key={i} x={xs(i)-2} y={ch+10} width={4} height={vs(d.volume||0)} fill={d.close>=(data[i-1]?.close||d.open)?'#10b98140':'#ef444440'} rx="1"/>)}
            <path d={area} fill={`url(#ag-${symbol})`}/>
            {maPath&&<path d={maPath} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeDasharray="4,2" opacity="0.7"/>}
            <path d={line} fill="none" stroke={tc} strokeWidth="2.5" strokeLinecap="round" filter="url(#gl2)"/>
            {[0,data.length-1,...(hov!==null?[hov]:[])].filter((v,i,a)=>a.indexOf(v)===i&&v>=0&&v<data.length).map(i=>(
              <circle key={i} cx={xs(i)} cy={ys(data[i].close)} r={i===hov?8:5} fill={i===hov?tc:'#0f172a'} stroke={tc} strokeWidth={i===hov?3:2}/>
            ))}
            {hov!==null&&<>
              <line x1={xs(hov)} y1={0} x2={xs(hov)} y2={ch+55} stroke="#475569" strokeWidth="1" strokeDasharray="4,4"/>
              <line x1={0} y1={ys(data[hov].close)} x2={cw} y2={ys(data[hov].close)} stroke="#475569" strokeWidth="1" strokeDasharray="4,4"/>
              <g transform={`translate(${cw+5},${ys(data[hov].close)})`}>
                <rect x="0" y="-10" width="58" height="20" fill={tc} rx="4"/>
                <text x="29" y="4" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold">${data[hov].close.toFixed(2)}</text>
              </g>
            </>}
            {data.filter((_,i)=>i%Math.ceil(data.length/6)===0||i===data.length-1).map(d=>{
              const i=data.indexOf(d);
              return <text key={i} x={xs(i)} y={ch+68} textAnchor="middle" fill="#64748b" fontSize="11">{fmtD(d.date)}</text>;
            })}
          </g>
        </svg>
        <div style={{display:'flex',justifyContent:'center',gap:'1.5rem',marginTop:'0.75rem',flexWrap:'wrap'}}>
          {[{c:tc,l:'Price'},{c:'#8b5cf6',l:'20-Day MA',d:true},{c:'#f59e0b',l:'Average',d:true},{c:'rgba(16,185,129,0.5)',l:'Volume'}].map(({c,l,d})=>(
            <div key={l} style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <div style={{width:18,height:2.5,background:c,borderRadius:2,opacity:d?0.7:1}}/>
              <span style={{fontSize:'11px',color:'#94a3b8'}}>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FULL STOCK DETAIL PANEL — mirrors CompanyAnalysis tabs
// Overview / Financials / Earnings / Health / News / Sentiment
// ═══════════════════════════════════════════════════════════════════
function StockDetailPanel({ s, onClose, onTrade }) {
  const { w } = useWindowSize();
  const isMobile = w < 768;

  const [tab, setTab] = useState('overview');
  const [ov, setOv] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [priceLoading, setPriceLoading] = useState(false);
  const [timeframe, setTimeframe] = useState('daily');
  const [income, setIncome] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [finPeriod, setFinPeriod] = useState('quarterly');
  const [finLoading, setFinLoading] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [earnLoading, setEarnLoading] = useState(false);
  const [earnError, setEarnError] = useState(null);
  const [news, setNews] = useState([]);
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial data load
  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/company/${s.ticker}/overview`).then(r=>r.json()).catch(()=>null),
      fetch(`${API_URL}/stocks/${s.ticker}/prices/daily`).then(r=>r.json()).catch(()=>null),
      fetch(`${API_URL}/news/${s.ticker}`).then(r=>r.json()).catch(()=>null),
      fetch(`${API_URL}/reddit/sentiment/${s.ticker}`).then(r=>r.json()).catch(()=>null),
    ]).then(([ovRes, prRes, newsRes, sentRes]) => {
      if (ovRes?.success !== false) setOv(ovRes?.data || ovRes);
      const pd = prRes?.data || prRes?.prices || prRes;
      if (Array.isArray(pd)) setPriceData(pd);
      const articles = newsRes?.articles || newsRes?.data || newsRes;
      if (Array.isArray(articles)) setNews(articles.slice(0,10));
      if (sentRes?.success !== false) setSentiment(sentRes?.data?.analysis || sentRes?.analysis || null);
      setLoading(false);
    });
  }, [s.ticker]);

  // Price on timeframe change
  useEffect(() => {
    if (!s.ticker) return;
    setPriceLoading(true);
    fetch(`${API_URL}/stocks/${s.ticker}/prices/${timeframe}`)
      .then(r=>r.json())
      .then(d=>{ const pd=d?.data||d?.prices||d; if(Array.isArray(pd)) setPriceData(pd); })
      .catch(()=>{})
      .finally(()=>setPriceLoading(false));
  }, [timeframe, s.ticker]);

  // Earnings on demand — lazy loaded when tab is clicked
  // Handles ALL known backend response shapes
  useEffect(() => {
    if (tab !== 'earnings') return;
    if (earnings) return; // already loaded
    setEarnLoading(true);
    setEarnError(null);
    console.log(`📊 Fetching earnings for ${s.ticker}...`);
    fetch(`${API_URL}/company/${s.ticker}/earnings`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(res => {
        console.log('📊 Earnings raw response keys:', Object.keys(res || {}));
        console.log('📊 Earnings raw response:', JSON.stringify(res).substring(0, 500));

        // Unwrap one level — backend wraps in { success, data }
        const d = res?.data ?? res;
        console.log('📊 Unwrapped data keys:', Object.keys(d || {}));

        // Normalize to { quarterlyEarnings: [], annualEarnings: [] }
        // Backend may use: quarterlyEarnings, quarters, quarterly, annualEarnings, annual
        const normalized = {
          quarterlyEarnings:
            d?.quarterlyEarnings ||
            d?.quarters ||
            d?.quarterly ||
            (Array.isArray(d) ? d : null) ||
            [],
          annualEarnings:
            d?.annualEarnings ||
            d?.annual ||
            d?.annualData ||
            [],
        };

        console.log('📊 Normalized earnings:', {
          quarterly: normalized.quarterlyEarnings.length,
          annual: normalized.annualEarnings.length,
          sampleQ: normalized.quarterlyEarnings[0],
        });

        if (normalized.quarterlyEarnings.length > 0 || normalized.annualEarnings.length > 0) {
          setEarnings(normalized);
        } else {
          console.warn('⚠️ No earnings arrays found. Full response:', res);
          setEarnError('No earnings data returned for ' + s.ticker);
        }
      })
      .catch(err => {
        console.error('❌ Earnings fetch error:', err);
        setEarnError(err.message);
      })
      .finally(() => setEarnLoading(false));
  }, [tab, s.ticker]);

  // Financials on demand
  useEffect(() => {
    if (tab !== 'financials') return;
    setFinLoading(true);
    setIncome([]); setCashFlow([]);
    Promise.all([
      fetch(`${API_URL}/company/${s.ticker}/income-statement?period=${finPeriod}`).then(r=>r.json()).catch(()=>null),
      fetch(`${API_URL}/company/${s.ticker}/cash-flow?period=${finPeriod}`).then(r=>r.json()).catch(()=>null),
    ]).then(([inc, cf]) => {
      if (inc?.success) setIncome(inc.data?.statements || []);
      if (cf?.success) setCashFlow(cf.data?.statements || []);
      setFinLoading(false);
    });
  }, [tab, finPeriod, s.ticker]);

  const health = calcHealth(ov);
  const hc = health >= 70 ? '#10b981' : health >= 50 ? '#f59e0b' : '#ef4444';
  const pos = s.change >= 0;

  const card = (label, value, color='#60a5fa') => (
    <div style={{background:'rgba(15,23,42,0.6)',border:`1px solid ${color}33`,borderRadius:'10px',padding:'12px 14px'}}>
      <div style={{fontSize:'10px',color:'#475569',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'4px'}}>{label}</div>
      <div style={{fontSize:'18px',fontWeight:800,color}}>{value}</div>
    </div>
  );

  const TABS = [
    {id:'overview',  label:'📈 Overview'},
    {id:'financials',label:'💰 Financials'},
    {id:'earnings',  label:'📊 Earnings'},
    {id:'health',    label:'❤️ Health'},
    {id:'news',      label:'📰 News'},
    {id:'sentiment', label:'💬 Sentiment'},
  ];

  return (
    <div style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,0.92)',backdropFilter:'blur(20px)',
      display:'flex',alignItems:isMobile?'flex-end':'center',justifyContent:'center',padding:isMobile?0:'20px'}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'linear-gradient(160deg,#0d1829 0%,#0f172a 100%)',
        border:`1px solid ${s.color}44`,
        borderRadius:isMobile?'24px 24px 0 0':'24px',
        width:'100%', maxWidth:isMobile?'100%':'860px',
        maxHeight:isMobile?'94dvh':'90vh',
        overflowY:'auto',display:'flex',flexDirection:'column',
        boxShadow:`0 40px 120px ${s.color}22`,
        fontFamily:'system-ui,-apple-system,sans-serif',
      }}>

        {/* ── PANEL HEADER ─────────────────────────────────────────────── */}
        <div style={{background:`linear-gradient(135deg,${s.color}18,transparent)`,
          borderBottom:`1px solid ${s.color}22`,
          padding:isMobile?'16px 16px 12px':'24px 28px 18px',flexShrink:0,position:'relative'}}>
          <div style={{position:'absolute',top:-20,right:-10,fontSize:120,opacity:0.04,pointerEvents:'none',userSelect:'none'}}>{s.emoji}</div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
            <div style={{display:'flex',gap:'14px',alignItems:'center'}}>
              <div style={{width:52,height:52,borderRadius:'14px',
                background:`linear-gradient(135deg,${s.color}44,${s.color}22)`,
                border:`1px solid ${s.color}55`,
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:26}}>{s.emoji}</div>
              <div>
                <div style={{fontSize:isMobile?20:26,fontWeight:900,color:'#fff',letterSpacing:'-0.02em'}}>{s.ticker}</div>
                <div style={{fontSize:12,color:'#64748b'}}>{s.name} · {s.exchange} · {s.sector}</div>
              </div>
            </div>
            <button onClick={onClose} style={{background:'rgba(255,255,255,0.06)',border:'none',color:'#94a3b8',
              width:34,height:34,borderRadius:'8px',cursor:'pointer',fontSize:16,flexShrink:0}}>✕</button>
          </div>
          {/* Price row */}
          <div style={{display:'flex',gap:'16px',alignItems:'flex-end',marginTop:'14px',flexWrap:'wrap'}}>
            <div style={{fontSize:isMobile?28:36,fontWeight:900,color:'#fff',letterSpacing:'-0.03em',lineHeight:1}}>${s.price.toFixed(2)}</div>
            <span style={{fontSize:'14px',fontWeight:800,
              color:pos?'#00ffaa':'#ff4466',
              background:pos?'rgba(0,255,170,0.1)':'rgba(255,68,102,0.1)',
              padding:'4px 10px',borderRadius:'8px',border:`1px solid ${pos?'#00ffaa33':'#ff446633'}`}}>
              {pos?'▲':'▼'} {Math.abs(s.change).toFixed(2)}%
            </span>
            <span style={{fontSize:'12px',color:'#64748b',marginBottom:'4px'}}>{pos?'+':''}{s.changeAmt.toFixed(2)} today</span>
            <div style={{
              marginLeft:'auto',padding:'8px 16px',borderRadius:'10px',
              background:`${s.color}18`,border:`1px solid ${s.color}44`,
              color:s.color,fontSize:'11px',fontWeight:800,letterSpacing:'0.06em',textTransform:'uppercase',
              display:'flex',alignItems:'center',gap:'6px'}}>
              <span>🔬</span> Analysis Mode
            </div>
          </div>
          {/* Quick stats bar */}
          <div style={{display:'flex',gap:'20px',marginTop:'12px',flexWrap:'wrap'}}>
            {[
              {l:'Mkt Cap',v:ov?.marketCap?`$${fmtNum(ov.marketCap)}`:s.mktCap},
              {l:'Volume',v:s.vol},
              {l:'P/E',v:ov?.peRatio?`${ov.peRatio.toFixed(2)}x`:s.pe?`${s.pe}x`:'—'},
              {l:'52W High',v:ov?.week52High?`$${ov.week52High.toFixed(2)}`:'—'},
              {l:'52W Low',v:ov?.week52Low?`$${ov.week52Low.toFixed(2)}`:'—'},
              {l:'Beta',v:ov?.beta?.toFixed(2)||'—'},
              {l:'EPS',v:ov?.eps?`$${ov.eps.toFixed(2)}`:'—'},
              {l:'Dividend',v:ov?.dividendYield?`${(ov.dividendYield*100).toFixed(2)}%`:'—'},
            ].map(({l,v})=>(
              <div key={l}>
                <div style={{fontSize:'9px',color:'#475569',textTransform:'uppercase',letterSpacing:'0.1em'}}>{l}</div>
                <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1'}}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── TAB BAR ────────────────────────────────────────────────────── */}
        <div style={{display:'flex',gap:'2px',padding:'10px 16px 0',overflowX:'auto',flexShrink:0,
          borderBottom:'1px solid #1e293b',WebkitOverflowScrolling:'touch',scrollbarWidth:'none'}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={{
              padding:'8px 14px',borderRadius:'8px 8px 0 0',border:'none',cursor:'pointer',whiteSpace:'nowrap',
              background:tab===t.id?`${s.color}22`:'transparent',
              color:tab===t.id?s.color:'#64748b',
              fontSize:'11px',fontWeight:700,letterSpacing:'0.04em',
              borderBottom:tab===t.id?`2px solid ${s.color}`:'2px solid transparent',
              transition:'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>

        {/* ── TAB CONTENT ────────────────────────────────────────────────── */}
        <div style={{padding:isMobile?'16px':'24px 28px',flex:1,overflowY:'visible'}}>
          {loading ? (
            <div style={{textAlign:'center',padding:'4rem',color:'#64748b'}}>
              <div style={{fontSize:40,animation:'spin 1s linear infinite',display:'inline-block'}}>⟳</div>
              <style>{'@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'}</style>
              <div style={{marginTop:'1rem'}}>Loading {s.ticker} data...</div>
            </div>
          ) : <>

          {/* ────────────── OVERVIEW ─────────────────────────────────────── */}
          {tab==='overview' && (
            <div>
              {/* Timeframe buttons */}
              <div style={{display:'flex',gap:'6px',marginBottom:'16px',flexWrap:'wrap'}}>
                {['daily','weekly','monthly'].map(tf=>(
                  <button key={tf} onClick={()=>setTimeframe(tf)} style={{
                    padding:'6px 14px',borderRadius:'6px',cursor:'pointer',textTransform:'capitalize',
                    border:`1px solid ${timeframe===tf?s.color:'#334155'}`,
                    background:timeframe===tf?`${s.color}22`:'#1e293b',
                    color:timeframe===tf?s.color:'#94a3b8',
                    fontSize:'11px',fontWeight:700,transition:'all 0.15s',
                  }}>{tf}</button>
                ))}
              </div>

              {/* Price chart */}
              <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'20px'}}>
                {priceLoading ? (
                  <div style={{padding:'3rem',textAlign:'center',color:'#94a3b8'}}>Loading price data...</div>
                ) : priceData && priceData.length > 0 ? (
                  <SportsPriceChart data={priceData} symbol={s.ticker} timeframe={timeframe}/>
                ) : (
                  <div style={{padding:'3rem',textAlign:'center',color:'#64748b'}}>No price data available</div>
                )}
              </div>

              {/* 8-metric grid */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px',marginBottom:'20px'}}>
                {card('P/E Ratio', ov?.peRatio?`${ov.peRatio.toFixed(2)}x`:s.pe?`${s.pe}x`:'N/A', '#60a5fa')}
                {card('EPS', ov?.eps?`$${ov.eps.toFixed(2)}`:'N/A', '#10b981')}
                {card('52W High', ov?.week52High?`$${ov.week52High.toFixed(2)}`:'N/A', '#10b981')}
                {card('52W Low', ov?.week52Low?`$${ov.week52Low.toFixed(2)}`:'N/A', '#ef4444')}
                {card('Market Cap', ov?.marketCap?`$${fmtNum(ov.marketCap)}`:s.mktCap, s.color)}
                {card('Beta', ov?.beta?.toFixed(2)||'N/A', '#f59e0b')}
                {card('Dividend Yield', ov?.dividendYield?`${(ov.dividendYield*100).toFixed(2)}%`:'N/A', '#8b5cf6')}
                {card('Profit Margin', ov?.profitMargin?`${(ov.profitMargin*100).toFixed(1)}%`:'N/A', '#00d4aa')}
              </div>

              {/* Profitability metrics */}
              {ov && (
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#10b981',marginBottom:'14px',display:'flex',alignItems:'center',gap:'6px'}}>📊 Profitability Metrics</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:'10px'}}>
                    {[
                      {l:'Profit Margin',v:`${((ov.profitMargin||0)*100).toFixed(2)}%`},
                      {l:'Operating Margin',v:`${((ov.operatingMargin||0)*100).toFixed(2)}%`},
                      {l:'Return on Equity',v:`${((ov.returnOnEquity||0)*100).toFixed(2)}%`},
                      {l:'Return on Assets',v:`${((ov.returnOnAssets||0)*100).toFixed(2)}%`},
                    ].map(({l,v})=>(
                      <div key={l} style={{background:'rgba(15,23,42,0.5)',borderRadius:'8px',padding:'10px 12px'}}>
                        <div style={{fontSize:'10px',color:'#475569',textTransform:'uppercase',marginBottom:'3px'}}>{l}</div>
                        <div style={{fontSize:'15px',fontWeight:700,color:'#00d4aa'}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Valuation ratios */}
              {ov && (
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#60a5fa',marginBottom:'14px'}}>💹 Valuation Ratios</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px'}}>
                    {[
                      {l:'P/E Ratio',v:ov.peRatio?.toFixed(2)||'N/A',c:'#60a5fa'},
                      {l:'PEG Ratio',v:ov.pegRatio?.toFixed(2)||'N/A',c:'#10b981'},
                      {l:'Price/Book',v:ov.priceToBook?.toFixed(2)||'N/A',c:'#8b5cf6'},
                      {l:'Price/Sales',v:ov.priceToSales?.toFixed(2)||'N/A',c:'#f59e0b'},
                      {l:'EV/Revenue',v:ov.evToRevenue?.toFixed(2)||'N/A',c:'#ff6b9d'},
                      {l:'EV/EBITDA',v:ov.evToEbitda?.toFixed(2)||'N/A',c:'#00d4aa'},
                    ].map(({l,v,c})=>(
                      <div key={l} style={{background:'rgba(15,23,42,0.5)',border:`1px solid ${c}22`,borderRadius:'8px',padding:'10px 12px'}}>
                        <div style={{fontSize:'10px',color:'#475569',textTransform:'uppercase',marginBottom:'3px'}}>{l}</div>
                        <div style={{fontSize:'16px',fontWeight:800,color:c}}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Company description */}
              {ov?.description && (
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#94a3b8',marginBottom:'10px'}}>📋 About {ov.name||s.name}</div>
                  <p style={{fontSize:'12px',color:'#94a3b8',lineHeight:'1.7',margin:0}}>{ov.description}</p>
                </div>
              )}

              {/* Alpha Intelligence™ */}
              <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px'}}>
                <AlphaIntelligencePanel symbol={s.ticker} />
              </div>
            </div>
          )}

          {/* ────────────── FINANCIALS ───────────────────────────────────── */}
          {tab==='financials' && (
            <div>
              <div style={{display:'flex',gap:'6px',marginBottom:'18px'}}>
                {['quarterly','annual'].map(p=>(
                  <button key={p} onClick={()=>{setFinPeriod(p);setIncome([]);}} style={{
                    padding:'7px 16px',borderRadius:'8px',border:'none',cursor:'pointer',textTransform:'capitalize',
                    background:finPeriod===p?'#8b5cf6':'rgba(255,255,255,0.04)',
                    color:'white',fontSize:'12px',fontWeight:700}}>{p}</button>
                ))}
              </div>
              {finLoading||income.length===0 ? (
                <div style={{textAlign:'center',padding:'3rem',color:'#64748b'}}>
                  {finLoading?'Loading financials...':'No financial data available'}
                </div>
              ) : <>
                {/* Revenue trend */}
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#10b981',marginBottom:'14px'}}>📈 Revenue Trend</div>
                  {income.slice(0,8).reverse().map((d,i,arr)=>{
                    const max=Math.max(...arr.map(x=>x.revenue||0))||1;
                    const pct=((d.revenue||0)/max*100);
                    return (
                      <div key={i} style={{marginBottom:'8px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                          <span style={{fontSize:'11px',color:'#64748b'}}>{d.fiscalDate?.substring(0,7)}</span>
                          <span style={{fontSize:'11px',fontWeight:700,color:'#10b981'}}>${fmtNum(d.revenue)}</span>
                        </div>
                        <div style={{height:'7px',background:'rgba(15,23,42,0.8)',borderRadius:'4px'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:'linear-gradient(90deg,#10b981,#10b981cc)',borderRadius:'4px',transition:'width 0.6s ease'}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Net income */}
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#60a5fa',marginBottom:'14px'}}>💵 Net Income</div>
                  {income.slice(0,8).reverse().map((d,i,arr)=>{
                    const max=Math.max(...arr.map(x=>Math.abs(x.netIncome||0)))||1;
                    const pct=(Math.abs(d.netIncome||0)/max*100);
                    const pos=(d.netIncome||0)>=0;
                    return (
                      <div key={i} style={{marginBottom:'8px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
                          <span style={{fontSize:'11px',color:'#64748b'}}>{d.fiscalDate?.substring(0,7)}</span>
                          <span style={{fontSize:'11px',fontWeight:700,color:pos?'#60a5fa':'#ef4444'}}>${fmtNum(d.netIncome)}</span>
                        </div>
                        <div style={{height:'7px',background:'rgba(15,23,42,0.8)',borderRadius:'4px'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:`linear-gradient(90deg,${pos?'#60a5fa':'#ef4444'},${pos?'#60a5facc':'#ef4444cc'})`,borderRadius:'4px'}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Profit margins table */}
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px',overflowX:'auto'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1',marginBottom:'14px'}}>📊 Profit Margins</div>
                  <table style={{width:'100%',borderCollapse:'collapse',minWidth:'380px'}}>
                    <thead><tr style={{borderBottom:'1px solid #334155'}}>
                      {['Period','Gross %','Operating %','Net %'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',fontSize:'10px',color:'#64748b',textTransform:'uppercase',textAlign:h==='Period'?'left':'right'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {income.slice(0,8).map((d,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #1e293b'}}>
                          <td style={{padding:'8px 10px',fontSize:'12px',color:'#94a3b8',fontWeight:600}}>{d.fiscalDate?.substring(0,7)}</td>
                          {[{v:d.grossProfitMargin,c:'#10b981'},{v:d.operatingMargin,c:'#60a5fa'},{v:d.netProfitMargin,c:'#8b5cf6'}].map(({v,c},j)=>(
                            <td key={j} style={{padding:'8px 10px',fontSize:'12px',fontWeight:700,color:c,textAlign:'right'}}>
                              {v!=null?`${(v*100).toFixed(1)}%`:'N/A'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Cash flow */}
                {cashFlow.length>0&&(
                  <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',overflowX:'auto'}}>
                    <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1',marginBottom:'14px'}}>💸 Cash Flow Statement</div>
                    <table style={{width:'100%',borderCollapse:'collapse',minWidth:'380px'}}>
                      <thead><tr style={{borderBottom:'1px solid #334155'}}>
                        {['Period','Operating','Investing','Financing'].map(h=>(
                          <th key={h} style={{padding:'8px 10px',fontSize:'10px',color:'#64748b',textTransform:'uppercase',textAlign:h==='Period'?'left':'right'}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {cashFlow.slice(0,8).map((d,i)=>(
                          <tr key={i} style={{borderBottom:'1px solid #1e293b'}}>
                            <td style={{padding:'8px 10px',fontSize:'12px',color:'#94a3b8',fontWeight:600}}>{d.fiscalDate?.substring(0,7)}</td>
                            {[{v:d.operatingCashFlow,c:'#10b981'},{v:d.investingCashFlow,c:'#ef4444'},{v:d.financingCashFlow,c:'#60a5fa'}].map(({v,c},j)=>(
                              <td key={j} style={{padding:'8px 10px',fontSize:'12px',fontWeight:700,color:v>=0?c:'#ef4444',textAlign:'right'}}>
                                {v!=null?`$${fmtNum(v)}`:'N/A'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>}
            </div>
          )}

          {/* ────────────── EARNINGS ─────────────────────────────────────── */}
          {tab==='earnings' && (() => {
            if (earnLoading) return (
              <div style={{textAlign:'center',padding:'3rem'}}>
                <div style={{fontSize:28,animation:'spin 1s linear infinite',display:'inline-block',color:'#60a5fa'}}>⟳</div>
                <p style={{color:'#94a3b8',marginTop:'12px',fontSize:'13px'}}>Loading earnings data for {s.ticker}...</p>
              </div>
            );
            if (earnError) return (
              <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'14px',padding:'20px',textAlign:'center'}}>
                <div style={{fontSize:24,marginBottom:'8px'}}>⚠️</div>
                <div style={{color:'#f87171',fontSize:'13px',marginBottom:'12px'}}>Failed to load earnings: {earnError}</div>
                <button onClick={()=>setEarnings(null)||setEarnError(null)} 
                  style={{padding:'6px 16px',borderRadius:'6px',background:'#334155',border:'1px solid #475569',color:'#cbd5e1',cursor:'pointer',fontSize:'12px'}}>
                  Retry
                </button>
              </div>
            );
            const quarterly = earnings?.quarterlyEarnings || [];
            const annual = earnings?.annualEarnings || [];
            const hasData = quarterly.length > 0 || annual.length > 0;
            return (
            <div>
              {!hasData ? (
                <div style={{textAlign:'center',padding:'3rem',color:'#64748b'}}>
                  <div style={{fontSize:32,marginBottom:'12px'}}>📊</div>
                  No earnings data available for {s.ticker}
                </div>
              ) : (
                <>
                {/* Quarterly EPS */}
                {quarterly.length > 0 && (
                  <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px',marginBottom:'16px'}}>
                    <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1',marginBottom:'16px'}}>📊 Quarterly EPS — Beat / Miss vs. Estimate</div>
                    {quarterly.slice(0,8).map((e,i)=>{
                      const actual = parseFloat(e.reportedEPS ?? e.eps ?? 0);
                      const est = parseFloat(e.estimatedEPS ?? e.estimate ?? actual);
                      const surprisePct = parseFloat(e.surprisePercentage ?? (est ? (actual-est)/Math.abs(est)*100 : 0));
                      const beat = actual >= est;
                      const maxBar = Math.max(...quarterly.slice(0,8).map(x=>Math.abs(parseFloat(x.reportedEPS||0)))) || 1;
                      return (
                        <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px 0',borderBottom:'1px solid #1e293b'}}>
                          <div style={{width:'76px',flexShrink:0}}>
                            <div style={{fontSize:'11px',fontWeight:600,color:'#94a3b8'}}>{(e.fiscalDateEnding||e.date||'').substring(0,7)}</div>
                            <div style={{fontSize:'9px',color:'#475569'}}>{e.reportedDate||''}</div>
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:'flex',justifyContent:'space-between',marginBottom:'4px',flexWrap:'wrap',gap:'4px'}}>
                              <span style={{fontSize:'11px',fontWeight:700,color:beat?'#10b981':'#ef4444'}}>{beat?'✓ Beat':'✗ Miss'}</span>
                              <span style={{fontSize:'11px',color:'#64748b'}}>
                                Est: <span style={{color:'#94a3b8',fontWeight:600}}>${isNaN(est)?'—':est.toFixed(2)}</span>
                                {' '}·{' '}
                                Act: <span style={{color:beat?'#10b981':'#ef4444',fontWeight:700}}>${isNaN(actual)?'—':actual.toFixed(2)}</span>
                              </span>
                            </div>
                            <div style={{height:'5px',background:'rgba(15,23,42,0.8)',borderRadius:'3px',overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${Math.min(100,(Math.abs(actual)/maxBar)*100)}%`,
                                background:beat?'#10b981':'#ef4444',borderRadius:'3px',transition:'width 0.4s ease'}}/>
                            </div>
                          </div>
                          <div style={{width:'56px',textAlign:'right',flexShrink:0,fontSize:'12px',fontWeight:800,
                            color:surprisePct>=0?'#10b981':'#ef4444'}}>
                            {isNaN(surprisePct)?'—':`${surprisePct>=0?'+':''}${surprisePct.toFixed(1)}%`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Annual EPS */}
                {annual.length > 0 && (
                  <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px'}}>
                    <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1',marginBottom:'14px'}}>📅 Annual EPS History</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:'10px'}}>
                      {annual.slice(0,6).map((e,i)=>{
                        const eps = parseFloat(e.reportedEPS ?? e.eps ?? 0);
                        const pos = eps >= 0;
                        const maxEps = Math.max(...annual.slice(0,6).map(x=>Math.abs(parseFloat(x.reportedEPS||0)))) || 1;
                        return (
                          <div key={i} style={{background:'rgba(15,23,42,0.6)',border:`1px solid ${pos?'#10b98133':'#ef444433'}`,borderRadius:'10px',padding:'12px'}}>
                            <div style={{fontSize:'10px',color:'#475569',marginBottom:'5px'}}>{(e.fiscalDateEnding||'').substring(0,4)}</div>
                            <div style={{fontSize:'20px',fontWeight:900,color:pos?'#10b981':'#ef4444',marginBottom:'6px'}}>
                              {isNaN(eps)?'—':`${pos?'+':''}$${Math.abs(eps).toFixed(2)}`}
                            </div>
                            <div style={{height:'4px',background:'rgba(15,23,42,0.8)',borderRadius:'2px',overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${(Math.abs(eps)/maxEps)*100}%`,background:pos?'#10b981':'#ef4444',borderRadius:'2px'}}/>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                </>
              )}
            </div>
            );
          })()}

          {/* ────────────── HEALTH SCORE ─────────────────────────────────── */}
          {tab==='health' && (
            <div>
              {!ov ? (
                <div style={{textAlign:'center',padding:'3rem',color:'#64748b'}}>No fundamental data available for {s.ticker}</div>
              ) : <>
                {/* Score card */}
                <div style={{background:`linear-gradient(135deg,${hc}20,${hc}10)`,border:`2px solid ${hc}40`,
                  borderRadius:'14px',padding:'24px',textAlign:'center',marginBottom:'20px'}}>
                  <div style={{fontSize:'11px',color:'#94a3b8',marginBottom:'6px',textTransform:'uppercase',letterSpacing:'0.1em'}}>Overall Financial Health Score</div>
                  <div style={{fontSize:'64px',fontWeight:900,color:hc,lineHeight:1}}>{health}</div>
                  <div style={{fontSize:'16px',color:'#cbd5e1',marginTop:'8px'}}>
                    {health>=70?'🟢 Excellent':health>=50?'🟡 Good':'🔴 Needs Improvement'}
                  </div>
                </div>
                {/* Score bars */}
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'20px',marginBottom:'16px'}}>
                  <div style={{fontSize:'13px',fontWeight:700,color:'#cbd5e1',marginBottom:'16px'}}>Score Breakdown</div>
                  {[
                    {l:'Profitability',sc:Math.min(100,50+(ov.profitMargin>0.2?20:ov.profitMargin>0.1?10:0)+(ov.returnOnEquity>0.2?30:ov.returnOnEquity>0.1?15:0)),c:'#10b981'},
                    {l:'Valuation',sc:Math.min(100,50+(ov.peRatio&&ov.peRatio<20?20:0)+(ov.pegRatio&&ov.pegRatio<1.5?15:0)+(ov.priceToBook&&ov.priceToBook<3?15:0)),c:'#60a5fa'},
                    {l:'Growth',sc:Math.min(100,50+(ov.eps>0?20:0)+(ov.revenuePerShare>0?15:0)+(ov.operatingMargin>0.15?15:0)),c:'#8b5cf6'},
                  ].map(({l,sc,c})=>(
                    <div key={l} style={{marginBottom:'14px'}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:'5px'}}>
                        <span style={{fontSize:'12px',color:'#cbd5e1',fontWeight:600}}>{l}</span>
                        <span style={{fontSize:'12px',color:c,fontWeight:700}}>{sc}/100</span>
                      </div>
                      <div style={{height:'8px',background:'rgba(15,23,42,0.8)',borderRadius:'4px',overflow:'hidden'}}>
                        <div style={{height:'100%',width:`${sc}%`,background:`linear-gradient(90deg,${c},${c}cc)`,borderRadius:'4px',transition:'width 0.8s ease'}}/>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Detail metrics */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:'10px'}}>
                  {[
                    {l:'Profit Margin',v:`${((ov.profitMargin||0)*100).toFixed(1)}%`,c:'#10b981'},
                    {l:'Return on Equity',v:`${((ov.returnOnEquity||0)*100).toFixed(1)}%`,c:'#60a5fa'},
                    {l:'Return on Assets',v:`${((ov.returnOnAssets||0)*100).toFixed(1)}%`,c:'#8b5cf6'},
                    {l:'P/E Ratio',v:ov.peRatio?.toFixed(2)||'N/A',c:'#f59e0b'},
                    {l:'P/B Ratio',v:ov.priceToBook?.toFixed(2)||'N/A',c:'#ff6b9d'},
                    {l:'Debt/Equity',v:ov.debtToEquity?.toFixed(2)||'N/A',c:'#ef4444'},
                    {l:'Current Ratio',v:ov.currentRatio?.toFixed(2)||'N/A',c:'#00d4aa'},
                    {l:'Operating Margin',v:ov.operatingMargin?`${(ov.operatingMargin*100).toFixed(1)}%`:'N/A',c:'#a78bfa'},
                  ].map(({l,v,c})=>(
                    <div key={l} style={{background:'rgba(15,23,42,0.6)',border:`1px solid ${c}33`,borderRadius:'10px',padding:'12px'}}>
                      <div style={{fontSize:'10px',color:'#475569',textTransform:'uppercase',marginBottom:'4px'}}>{l}</div>
                      <div style={{fontSize:'20px',fontWeight:900,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>
              </>}
            </div>
          )}

          {/* ────────────── NEWS ─────────────────────────────────────────── */}
          {tab==='news' && (
            <div>
              {news.length===0 ? (
                <div style={{textAlign:'center',padding:'3rem',color:'#64748b'}}>No recent news found for {s.ticker}</div>
              ) : news.map((item,i)=>(
                <a key={i} href={item.url||item.link||'#'} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none',display:'block'}}>
                  <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'12px',
                    padding:'14px 16px',marginBottom:'10px',cursor:'pointer',transition:'border-color 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=`${s.color}66`}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='#334155'}>
                    <div style={{fontSize:'13px',fontWeight:600,color:'#e2e8f0',lineHeight:'1.5',marginBottom:'8px'}}>{item.title||'News Item'}</div>
                    <div style={{display:'flex',gap:'10px',flexWrap:'wrap',alignItems:'center'}}>
                      <span style={{fontSize:'10px',color:s.color,fontWeight:700,background:`${s.color}18`,padding:'2px 8px',borderRadius:'4px'}}>{item.source||'News'}</span>
                      <span style={{fontSize:'10px',color:'#475569'}}>{formatNewsDate(item.date||item.publishedAt||'')}</span>
                      {item.overall_sentiment_label&&(
                        <span style={{fontSize:'10px',fontWeight:700,padding:'2px 8px',borderRadius:'4px',
                          color:item.overall_sentiment_label.toLowerCase().includes('bull')?'#10b981':item.overall_sentiment_label.toLowerCase().includes('bear')?'#ef4444':'#f59e0b',
                          background:item.overall_sentiment_label.toLowerCase().includes('bull')?'rgba(16,185,129,0.12)':item.overall_sentiment_label.toLowerCase().includes('bear')?'rgba(239,68,68,0.12)':'rgba(245,158,11,0.12)'}}>
                          {item.overall_sentiment_label}
                        </span>
                      )}
                    </div>
                    {item.summary&&<p style={{fontSize:'11px',color:'#64748b',marginTop:'6px',lineHeight:'1.5',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{item.summary}</p>}
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* ────────────── REDDIT SENTIMENT ─────────────────────────────── */}
          {tab==='sentiment' && (
            <div>
              {!sentiment ? (
                <div style={{textAlign:'center',padding:'3rem',color:'#64748b'}}>
                  <div style={{fontSize:'2rem',marginBottom:'12px'}}>💬</div>
                  No Reddit sentiment data for {s.ticker}
                </div>
              ) : <>
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'20px',marginBottom:'16px',textAlign:'center'}}>
                  <div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:'8px'}}>Reddit / WallStreetBets Sentiment</div>
                  <div style={{fontSize:'28px',fontWeight:900,textTransform:'capitalize',marginBottom:'8px',
                    color:sentiment.overallSentiment==='bullish'?'#10b981':sentiment.overallSentiment==='bearish'?'#ef4444':'#94a3b8'}}>
                    {sentiment.overallSentiment}
                  </div>
                  <div style={{fontSize:'12px',color:'#64748b',lineHeight:'1.6',maxWidth:'520px',margin:'0 auto'}}>{sentiment.summary}</div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'10px',marginBottom:'16px'}}>
                  {[
                    {l:'Bullish',v:`${sentiment.bullishPercent||0}%`,c:'#10b981'},
                    {l:'Neutral',v:`${sentiment.neutralPercent||0}%`,c:'#64748b'},
                    {l:'Bearish',v:`${sentiment.bearishPercent||0}%`,c:'#ef4444'},
                  ].map(({l,v,c})=>(
                    <div key={l} style={{background:'rgba(15,23,42,0.6)',border:`1px solid ${c}33`,borderRadius:'10px',padding:'16px',textAlign:'center'}}>
                      <div style={{fontSize:'10px',color:'#475569',textTransform:'uppercase',marginBottom:'6px'}}>{l}</div>
                      <div style={{fontSize:'26px',fontWeight:900,color:c}}>{v}</div>
                    </div>
                  ))}
                </div>
                {sentiment.keyThemes?.length>0&&(
                  <div style={{marginBottom:'16px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                    {sentiment.keyThemes.map(t=>(
                      <span key={t} style={{fontSize:'11px',fontWeight:700,color:s.color,
                        background:`${s.color}18`,border:`1px solid ${s.color}33`,
                        padding:'4px 12px',borderRadius:'20px'}}>{t}</span>
                    ))}
                  </div>
                )}
                <div style={{background:'rgba(30,41,59,0.5)',border:'1px solid #334155',borderRadius:'14px',padding:'16px'}}>
                  <div style={{fontSize:'11px',color:'#64748b',fontWeight:600,marginBottom:'8px'}}>CONFIDENCE SCORE</div>
                  <div style={{height:'8px',background:'rgba(15,23,42,0.8)',borderRadius:'4px',marginBottom:'6px',overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${sentiment.confidence||0}%`,background:`linear-gradient(90deg,${s.color},${s.color}cc)`,borderRadius:'4px'}}/>
                  </div>
                  <div style={{fontSize:'12px',color:'#94a3b8'}}>{sentiment.confidence||0}% confidence · {sentiment.postsAnalyzed||0} posts analyzed</div>
                </div>
              </>}
            </div>
          )}

          </>}
        </div>
      </div>
    </div>
  );
}



// ── Securities data — 50 most important sports industry stocks ────────────────
const SECURITIES = [
  // ── FRANCHISE / TEAM OWNERS ──────────────────────────────────────────────────
  { ticker: "MANU",  name: "Manchester United",      sector: "Franchise",     exchange: "NYSE",   price: 15.24,  change:  3.12, changeAmt:  0.46, mktCap: "2.4B",   vol: "1.2M",  pe: 38.4, sport: "Soccer",        emoji: "⚽", color: "#e80000" },
  { ticker: "MSGS",  name: "MSG Sports",             sector: "Franchise",     exchange: "NYSE",   price: 182.40, change:  2.10, changeAmt:  3.74, mktCap: "3.1B",   vol: "412K",  pe: 22.1, sport: "Basketball",    emoji: "🏀", color: "#00B2FF" },
  { ticker: "BATRA", name: "Liberty Braves",         sector: "Franchise",     exchange: "NASDAQ", price: 38.72,  change: -0.84, changeAmt: -0.33, mktCap: "1.8B",   vol: "228K",  pe: 44.2, sport: "Baseball",      emoji: "⚾", color: "#C8102E" },
  { ticker: "FWONA", name: "Formula One Group",      sector: "Franchise",     exchange: "NASDAQ", price: 82.40,  change:  1.44, changeAmt:  1.18, mktCap: "21.2B",  vol: "680K",  pe: 38.2, sport: "Racing",        emoji: "🏎️", color: "#e10600" },
  { ticker: "MCLRF", name: "McLaren Racing",         sector: "Franchise",     exchange: "OTC",    price: 4.82,   change:  0.62, changeAmt:  0.03, mktCap: "1.1B",   vol: "42K",   pe: null, sport: "Racing",        emoji: "🧡", color: "#ff8000" },
  { ticker: "MSGE",  name: "MSG Entertainment",      sector: "Franchise",     exchange: "NYSE",   price: 38.90,  change:  0.52, changeAmt:  0.20, mktCap: "1.8B",   vol: "310K",  pe: null, sport: "Multi",         emoji: "🎡", color: "#1a6fc4" },
  { ticker: "BATRK", name: "Liberty Braves K",       sector: "Franchise",     exchange: "NASDAQ", price: 37.92,  change: -0.52, changeAmt: -0.20, mktCap: "1.7B",   vol: "180K",  pe: 42.8, sport: "Baseball",      emoji: "⚾", color: "#1e3a8a" },
  { ticker: "LSXMA", name: "Liberty SiriusXM",       sector: "Franchise",     exchange: "NASDAQ", price: 24.60,  change:  0.48, changeAmt:  0.12, mktCap: "9.8B",   vol: "890K",  pe: 18.2, sport: "Multi",         emoji: "🔵", color: "#00aaff" },

  // ── SPORTS BETTING ──────────────────────────────────────────────────────────
  { ticker: "DKNG",  name: "DraftKings",             sector: "Sports Betting", exchange: "NASDAQ", price: 41.20,  change: -1.30, changeAmt: -0.54, mktCap: "19.8B",  vol: "8.4M",  pe: null, sport: "Multi",         emoji: "🎰", color: "#53d337" },
  { ticker: "FLUT",  name: "Flutter Entertainment",  sector: "Sports Betting", exchange: "NYSE",   price: 228.50, change:  1.82, changeAmt:  4.14, mktCap: "41.2B",  vol: "520K",  pe: 31.2, sport: "Multi",         emoji: "🎲", color: "#ff6b35" },
  { ticker: "PENN",  name: "Penn Entertainment",     sector: "Sports Betting", exchange: "NASDAQ", price: 18.72,  change:  0.80, changeAmt:  0.15, mktCap: "2.9B",   vol: "3.1M",  pe: null, sport: "Multi",         emoji: "🃏", color: "#ff4499" },
  { ticker: "MGM",   name: "MGM Resorts",            sector: "Sports Betting", exchange: "NYSE",   price: 36.40,  change:  0.96, changeAmt:  0.35, mktCap: "10.8B",  vol: "4.2M",  pe: 18.4, sport: "Multi",         emoji: "♠️", color: "#c8aa6e" },
  { ticker: "CZR",   name: "Caesars Entertainment",  sector: "Sports Betting", exchange: "NASDAQ", price: 32.80,  change: -0.54, changeAmt: -0.18, mktCap: "7.1B",   vol: "5.8M",  pe: null, sport: "Multi",         emoji: "🎳", color: "#00bcd4" },
  { ticker: "WYNN",  name: "Wynn Resorts",           sector: "Sports Betting", exchange: "NASDAQ", price: 86.20,  change:  1.12, changeAmt:  0.96, mktCap: "9.4B",   vol: "1.4M",  pe: 24.2, sport: "Multi",         emoji: "🎯", color: "#8b5cf6" },
  { ticker: "RSI",   name: "Rush Street Interactive",sector: "Sports Betting", exchange: "NYSE",   price: 14.60,  change:  2.24, changeAmt:  0.32, mktCap: "2.8B",   vol: "1.8M",  pe: null, sport: "Multi",         emoji: "🏇", color: "#f59e0b" },
  { ticker: "EVRI",  name: "Everi Holdings",         sector: "Sports Betting", exchange: "NYSE",   price: 12.40,  change: -0.32, changeAmt: -0.04, mktCap: "1.3B",   vol: "1.1M",  pe: 14.8, sport: "Gaming",        emoji: "🎰", color: "#6366f1" },

  // ── SPORTSWEAR & EQUIPMENT ───────────────────────────────────────────────────
  { ticker: "NKE",   name: "Nike",                   sector: "Sportswear",    exchange: "NYSE",   price: 94.30,  change:  1.20, changeAmt:  1.12, mktCap: "144.2B", vol: "6.8M",  pe: 28.4, sport: "Multi",         emoji: "👟", color: "#ff6600" },
  { ticker: "ADDYY", name: "Adidas AG",              sector: "Sportswear",    exchange: "OTC",    price: 128.40, change:  2.14, changeAmt:  2.70, mktCap: "56.4B",  vol: "340K",  pe: 34.1, sport: "Multi",         emoji: "🏃", color: "#0066cc" },
  { ticker: "ONON",  name: "On Holdings",            sector: "Sportswear",    exchange: "NYSE",   price: 54.80,  change:  3.44, changeAmt:  1.82, mktCap: "17.4B",  vol: "1.9M",  pe: 68.2, sport: "Running",       emoji: "🏅", color: "#00e5ff" },
  { ticker: "UA",    name: "Under Armour",           sector: "Sportswear",    exchange: "NYSE",   price: 7.84,   change: -0.50, changeAmt: -0.04, mktCap: "3.4B",   vol: "3.8M",  pe: null, sport: "Multi",         emoji: "💪", color: "#1c1c1c" },
  { ticker: "SKX",   name: "Skechers USA",           sector: "Sportswear",    exchange: "NYSE",   price: 66.20,  change:  0.88, changeAmt:  0.58, mktCap: "9.8B",   vol: "820K",  pe: 15.4, sport: "Running",       emoji: "👠", color: "#4a90d9" },
  { ticker: "LULU",  name: "Lululemon Athletica",    sector: "Sportswear",    exchange: "NASDAQ", price: 284.60, change:  1.64, changeAmt:  4.60, mktCap: "36.2B",  vol: "1.2M",  pe: 28.8, sport: "Yoga/Fitness",  emoji: "🧘", color: "#a855f7" },
  { ticker: "CROX",  name: "Crocs Inc.",             sector: "Sportswear",    exchange: "NASDAQ", price: 112.40, change:  0.72, changeAmt:  0.80, mktCap: "6.8B",   vol: "1.4M",  pe: 11.2, sport: "Casual",        emoji: "🐊", color: "#84cc16" },
  { ticker: "DECK",  name: "Deckers Outdoor (HOKA)", sector: "Sportswear",    exchange: "NYSE",   price: 148.20, change:  2.10, changeAmt:  3.06, mktCap: "4.0B",   vol: "580K",  pe: 14.8, sport: "Running",       emoji: "🥾", color: "#f97316" },

  // ── SPORTS MEDIA & BROADCASTING ─────────────────────────────────────────────
  { ticker: "TKO",   name: "TKO Group (WWE/UFC)",    sector: "Sports Media",  exchange: "NYSE",   price: 148.20, change:  1.92, changeAmt:  2.82, mktCap: "22.4B",  vol: "890K",  pe: 52.4, sport: "Wrestling/MMA", emoji: "🥊", color: "#ff2244" },
  { ticker: "EDR",   name: "Endeavor Group",         sector: "Sports Media",  exchange: "NYSE",   price: 27.80,  change: -0.40, changeAmt: -0.11, mktCap: "10.2B",  vol: "1.4M",  pe: 41.2, sport: "Multi",         emoji: "🎬", color: "#aa44ff" },
  { ticker: "FOXA",  name: "Fox Corporation",        sector: "Sports Media",  exchange: "NASDAQ", price: 48.20,  change:  0.96, changeAmt:  0.46, mktCap: "27.4B",  vol: "3.2M",  pe: 14.4, sport: "Multi",         emoji: "🦊", color: "#ff6b35" },
  { ticker: "WBD",   name: "Warner Bros Discovery",  sector: "Sports Media",  exchange: "NASDAQ", price: 11.40,  change: -0.88, changeAmt: -0.10, mktCap: "27.8B",  vol: "18.4M", pe: null, sport: "Multi",         emoji: "📡", color: "#4ade80" },
  { ticker: "NFLX",  name: "Netflix (Sports Deals)", sector: "Sports Media",  exchange: "NASDAQ", price: 980.40, change:  2.14, changeAmt: 20.62, mktCap: "421B",   vol: "2.8M",  pe: 54.2, sport: "Multi",         emoji: "🎥", color: "#e50914" },
  { ticker: "DIS",   name: "Disney (ESPN)",          sector: "Sports Media",  exchange: "NYSE",   price: 108.40, change:  0.82, changeAmt:  0.88, mktCap: "196B",   vol: "9.4M",  pe: 38.2, sport: "Multi",         emoji: "🏰", color: "#1e40af" },
  { ticker: "SIRI",  name: "SiriusXM Holdings",      sector: "Sports Media",  exchange: "NASDAQ", price: 2.84,   change: -0.70, changeAmt: -0.02, mktCap: "10.4B",  vol: "14.2M", pe: 8.8,  sport: "Audio",         emoji: "📻", color: "#0ea5e9" },
  { ticker: "SLRY",  name: "Salary Cap Media",       sector: "Sports Media",  exchange: "OTC",    price: 2.40,   change:  1.27, changeAmt:  0.03, mktCap: "120M",   vol: "24K",   pe: null, sport: "Multi",         emoji: "📊", color: "#64748b" },

  // ── SPORTS TECHNOLOGY & ANALYTICS ───────────────────────────────────────────
  { ticker: "SRAD",  name: "Sportradar Group",       sector: "Sports Tech",   exchange: "NASDAQ", price: 22.40,  change:  1.82, changeAmt:  0.40, mktCap: "7.8B",   vol: "1.1M",  pe: null, sport: "Data/Analytics",emoji: "📈", color: "#06b6d4" },
  { ticker: "GENI",  name: "Genius Sports",          sector: "Sports Tech",   exchange: "NYSE",   price: 8.40,   change:  2.20, changeAmt:  0.18, mktCap: "1.6B",   vol: "1.4M",  pe: null, sport: "Data/Analytics",emoji: "🧠", color: "#8b5cf6" },
  { ticker: "HIMS",  name: "Hims & Hers Health",     sector: "Sports Tech",   exchange: "NYSE",   price: 22.40,  change:  2.14, changeAmt:  0.47, mktCap: "4.8B",   vol: "4.2M",  pe: null, sport: "Wellness",      emoji: "💊", color: "#00c896" },
  { ticker: "PLTK",  name: "Playtika Holding",       sector: "Sports Tech",   exchange: "NASDAQ", price: 3.82,   change: -0.52, changeAmt: -0.02, mktCap: "1.6B",   vol: "2.1M",  pe: null, sport: "Gaming",        emoji: "🕹️", color: "#f43f5e" },
  { ticker: "GAME",  name: "GameSquare Holdings",    sector: "Sports Tech",   exchange: "NASDAQ", price: 1.84,   change:  3.37, changeAmt:  0.06, mktCap: "180M",   vol: "720K",  pe: null, sport: "Esports",       emoji: "🎯", color: "#fbbf24" },

  // ── VENUES & INFRASTRUCTURE ──────────────────────────────────────────────────
  { ticker: "VICI",  name: "VICI Properties",        sector: "Venues",        exchange: "NYSE",   price: 32.10,  change:  1.24, changeAmt:  0.39, mktCap: "32.8B",  vol: "5.2M",  pe: 14.4, sport: "Multi",         emoji: "🎪", color: "#ff9900" },
  { ticker: "EPR",   name: "EPR Properties",         sector: "Venues",        exchange: "NYSE",   price: 44.20,  change:  0.68, changeAmt:  0.30, mktCap: "3.4B",   vol: "780K",  pe: 18.2, sport: "Multi",         emoji: "🏟️", color: "#ffcc00" },
  { ticker: "SFT",   name: "Shift4 Payments",        sector: "Venues",        exchange: "NYSE",   price: 84.20,  change:  1.44, changeAmt:  1.20, mktCap: "5.8B",   vol: "680K",  pe: 42.4, sport: "Payments",      emoji: "💳", color: "#4f46e5" },
  { ticker: "AMC",   name: "AMC Networks",           sector: "Venues",        exchange: "NASDAQ", price: 11.20,  change: -1.42, changeAmt: -0.16, mktCap: "640M",   vol: "1.2M",  pe: 4.8,  sport: "Entertainment", emoji: "🍿", color: "#dc2626" },

  // ── FITNESS & HEALTH ────────────────────────────────────────────────────────
  { ticker: "PTON",  name: "Peloton Interactive",    sector: "Fitness",       exchange: "NASDAQ", price: 6.84,   change: -1.15, changeAmt: -0.08, mktCap: "2.4B",   vol: "8.2M",  pe: null, sport: "Fitness",       emoji: "🚴", color: "#ff0066" },
  { ticker: "NLS",   name: "Nautilus Inc.",          sector: "Fitness",       exchange: "NYSE",   price: 1.42,   change: -2.74, changeAmt: -0.04, mktCap: "42M",    vol: "480K",  pe: null, sport: "Fitness",       emoji: "🏋️", color: "#0891b2" },
  { ticker: "XPOF",  name: "Xponential Fitness",     sector: "Fitness",       exchange: "NYSE",   price: 8.40,   change:  1.94, changeAmt:  0.16, mktCap: "480M",   vol: "620K",  pe: null, sport: "Fitness",       emoji: "💪", color: "#f59e0b" },
  { ticker: "PLNT",  name: "Planet Fitness",         sector: "Fitness",       exchange: "NYSE",   price: 84.20,  change:  0.96, changeAmt:  0.80, mktCap: "7.4B",   vol: "820K",  pe: 48.4, sport: "Fitness",       emoji: "🌍", color: "#ffd700" },

  // ── ESPORTS ────────────────────────────────────────────────────────────────
  { ticker: "SKLZ",  name: "Skillz Inc.",            sector: "Esports",       exchange: "NYSE",   price: 4.82,   change: -2.44, changeAmt: -0.12, mktCap: "420M",   vol: "2.1M",  pe: null, sport: "Esports",       emoji: "🎮", color: "#7c3aed" },
  { ticker: "GLXG",  name: "Galaxy Gaming",          sector: "Esports",       exchange: "OTC",    price: 3.14,   change:  1.62, changeAmt:  0.05, mktCap: "180M",   vol: "88K",   pe: null, sport: "Esports",       emoji: "👾", color: "#06b6d4" },
  { ticker: "GMBL",  name: "Esports Entertainment",  sector: "Esports",       exchange: "NASDAQ", price: 1.24,   change: -3.13, changeAmt: -0.04, mktCap: "48M",    vol: "1.8M",  pe: null, sport: "Esports",       emoji: "🕹️", color: "#a855f7" },
  { ticker: "NERD",  name: "Theta Lake",             sector: "Esports",       exchange: "NASDAQ", price: 2.84,   change: -1.04, changeAmt: -0.03, mktCap: "280M",   vol: "380K",  pe: null, sport: "Esports",       emoji: "🤓", color: "#10b981" },
  { ticker: "BOWL",  name: "Bowlero Corp",            sector: "Venues",        exchange: "NYSE",   price: 11.20,  change:  0.90, changeAmt:  0.10, mktCap: "2.1B",   vol: "640K",  pe: 28.4, sport: "Bowling/Ent",   emoji: "🎳", color: "#f59e0b" },
];

const SECTORS = ["ALL", "Franchise", "Sports Betting", "Sportswear", "Sports Media", "Sports Tech", "Venues", "Fitness", "Esports"];

// ── Sparkline generator ───────────────────────────────────────────────────────
function generateSparkData(positive, volatile = false) {
  const pts = [];
  let v = 50;
  for (let i = 0; i < 20; i++) {
    const drift = positive ? 0.4 : -0.4;
    const noise = (Math.random() - 0.5) * (volatile ? 12 : 6);
    v = Math.max(10, Math.min(90, v + drift + noise));
    pts.push(v);
  }
  return pts;
}

function Sparkline({ data, positive, width = 80, height = 32 }) {
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  const fillPts = `0,${height} ${pts} ${width},${height}`;
  const color = positive ? "#00ffaa" : "#ff4466";
  return (
    <svg width={width} height={height} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={`sg-${positive}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fillPts} fill={`url(#sg-${positive})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

// ── Animated ticker tape ──────────────────────────────────────────────────────
function TickerTape({ securities }) {
  const items = [...securities, ...securities];
  return (
    <div style={{ overflow: "hidden", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,0,0,0.4)", height: 36, display: "flex", alignItems: "center" }}>
      <div style={{ display: "flex", gap: 0, animation: "ticker 40s linear infinite", whiteSpace: "nowrap" }}>
        {items.map((s, i) => {
          const pos = s.change >= 0;
          return (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 24px", fontSize: 11, borderRight: "1px solid rgba(255,255,255,0.05)" }}>
              <span style={{ color: s.color, fontWeight: 800 }}>{s.emoji}</span>
              <span style={{ color: "#cbd5e1", fontWeight: 700, letterSpacing: "0.05em" }}>{s.ticker}</span>
              <span style={{ color: "#fff", fontWeight: 600 }}>${s.price.toFixed(2)}</span>
              <span style={{ color: pos ? "#00ffaa" : "#ff4466", fontWeight: 700 }}>{pos ? "▲" : "▼"} {Math.abs(s.change).toFixed(2)}%</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ── Hero stat card ────────────────────────────────────────────────────────────
function HeroStat({ label, value, sub, accent, icon, delay }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div style={{
      background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
      border: `1px solid ${accent}33`,
      borderRadius: 16,
      padding: "20px 22px",
      position: "relative",
      overflow: "hidden",
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(16px)",
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
    }}>
      <div style={{ position: "absolute", top: -20, right: -20, fontSize: 64, opacity: 0.06, userSelect: "none" }}>{icon}</div>
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)`, opacity: 0.6 }} />
      <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 8, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: accent, marginTop: 6, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

// ── Security card (grid view) ────────────────────────────────────────────────
function SecurityCard({ s, onTrade, onDetail, inWatchlist, onWatch, delay, sparkData }) {
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);
  const pos = s.change >= 0;
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onDetail(s)}
      style={{
        background: hovered
          ? `linear-gradient(135deg, ${s.color}12, rgba(255,255,255,0.04))`
          : "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))",
        border: `1px solid ${hovered ? s.color + "55" : "rgba(255,255,255,0.07)"}`,
        borderRadius: 18,
        padding: "20px",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        transform: hovered ? "translateY(-3px)" : visible ? "translateY(0)" : "translateY(20px)",
        opacity: visible ? 1 : 0,
        boxShadow: hovered ? `0 20px 60px ${s.color}22, 0 0 0 1px ${s.color}22` : "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Glow top edge */}
      <div style={{ position: "absolute", top: 0, left: "20%", right: "20%", height: 1, background: `linear-gradient(90deg, transparent, ${s.color}88, transparent)`, opacity: hovered ? 1 : 0, transition: "opacity 0.3s" }} />

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{
            width: 42, height: 42, borderRadius: 12,
            background: `linear-gradient(135deg, ${s.color}33, ${s.color}11)`,
            border: `1px solid ${s.color}44`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
            boxShadow: hovered ? `0 0 20px ${s.color}44` : "none",
            transition: "box-shadow 0.3s",
          }}>{s.emoji}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", letterSpacing: "-0.01em" }}>{s.ticker}</div>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 600 }}>{s.exchange}</div>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onWatch(s.ticker); }}
          style={{
            background: "none", border: "none", fontSize: 16, cursor: "pointer",
            color: inWatchlist ? "#ffd700" : "#333",
            filter: inWatchlist ? "drop-shadow(0 0 6px #ffd70088)" : "none",
            transition: "all 0.2s",
          }}
        >{inWatchlist ? "★" : "☆"}</button>
      </div>

      {/* Name + sector */}
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 14, fontWeight: 500 }}>{s.name}</div>
      <div style={{ display: "inline-block", fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: s.color, background: `${s.color}18`, border: `1px solid ${s.color}33`, borderRadius: 4, padding: "3px 8px", marginBottom: 14 }}>{s.sector.toUpperCase()}</div>

      {/* Price + change */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>${s.price.toFixed(2)}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: pos ? "#00ffaa" : "#ff4466" }}>{pos ? "▲" : "▼"} {Math.abs(s.change).toFixed(2)}%</span>
            <span style={{ fontSize: 11, color: pos ? "#00ffaa88" : "#ff446688" }}>{pos ? "+" : ""}{s.changeAmt.toFixed(2)}</span>
          </div>
        </div>
        <Sparkline data={sparkData} positive={pos} width={80} height={32} />
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        {[
          { label: "MKT CAP", value: s.mktCap },
          { label: "VOLUME", value: s.vol },
          { label: "P/E", value: s.pe ? `${s.pe}x` : "—" },
        ].map(({ label, value }) => (
          <div key={label} style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: "0.1em", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Deep Analysis button */}
      <button
        onClick={(e) => { e.stopPropagation(); onDetail(s); }}
        style={{
          width: "100%", padding: "11px", borderRadius: 10, cursor: "pointer",
          background: hovered ? `linear-gradient(135deg, ${s.color}22, ${s.color}11)` : "rgba(255,255,255,0.04)",
          border: hovered ? `1px solid ${s.color}66` : "1px solid rgba(255,255,255,0.08)",
          color: hovered ? s.color : "#475569",
          fontSize: 11, fontWeight: 800, letterSpacing: "0.08em",
          transition: "all 0.25s",
          textTransform: "uppercase",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          boxShadow: hovered ? `0 4px 16px ${s.color}22` : "none",
        }}
      >
        <span style={{fontSize: 13}}>🔬</span>
        {hovered ? `Deep Analysis →` : "Deep Analysis"}
      </button>
    </div>
  );
}

// ── Trade modal ───────────────────────────────────────────────────────────────
function TradeModal({ s, onClose }) {
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState("10");
  const [orderType, setOrderType] = useState("market");
  const [submitted, setSubmitted] = useState(false);
  const total = (parseFloat(qty) || 0) * s.price;
  const pos = s.change >= 0;

  const handleSubmit = () => { setSubmitted(true); setTimeout(onClose, 1800); };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(20px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "linear-gradient(160deg, #0d0f1a, #080a10)",
          border: `1px solid ${s.color}44`,
          borderRadius: 24, width: "100%", maxWidth: 440,
          boxShadow: `0 40px 120px ${s.color}22, 0 0 0 1px ${s.color}22`,
          overflow: "hidden",
          animation: "modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Modal header with gradient */}
        <div style={{ background: `linear-gradient(135deg, ${s.color}22, transparent)`, padding: "24px 28px", borderBottom: `1px solid ${s.color}22`, position: "relative" }}>
          <div style={{ position: "absolute", top: -30, right: -30, fontSize: 100, opacity: 0.05 }}>{s.emoji}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${s.color}44, ${s.color}22)`, border: `1px solid ${s.color}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, boxShadow: `0 0 30px ${s.color}44` }}>{s.emoji}</div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>{s.ticker}</div>
                <div style={{ fontSize: 12, color: "#64748b" }}>{s.name}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "none", color: "#64748b", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 14 }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", letterSpacing: "-0.03em" }}>${s.price.toFixed(2)}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: pos ? "#00ffaa" : "#ff4466", background: pos ? "rgba(0,255,170,0.1)" : "rgba(255,68,102,0.1)", padding: "4px 10px", borderRadius: 8, border: `1px solid ${pos ? "#00ffaa33" : "#ff446633"}` }}>
                {pos ? "▲" : "▼"} {Math.abs(s.change).toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        {submitted ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16, animation: "popIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)" }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 6 }}>Order Submitted!</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>{side === "buy" ? "Buying" : "Selling"} {qty} shares of {s.ticker} via Alpaca</div>
          </div>
        ) : (
          <div style={{ padding: 24 }}>
            {/* Buy/Sell */}
            <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: 3, marginBottom: 20 }}>
              {[
                { id: "buy", label: "BUY", color: "#00ffaa" },
                { id: "sell", label: "SELL", color: "#ff4466" },
              ].map(btn => (
                <button key={btn.id} onClick={() => setSide(btn.id)} style={{
                  flex: 1, padding: "10px", borderRadius: 10, border: "none", cursor: "pointer",
                  background: side === btn.id ? `linear-gradient(135deg, ${btn.color}33, ${btn.color}22)` : "transparent",
                  color: side === btn.id ? btn.color : "#444",
                  fontSize: 12, fontWeight: 900, letterSpacing: "0.1em",
                  boxShadow: side === btn.id ? `0 0 20px ${btn.color}33` : "none",
                  border: side === btn.id ? `1px solid ${btn.color}44` : "1px solid transparent",
                  transition: "all 0.2s",
                }}>{btn.label}</button>
              ))}
            </div>

            {/* Order type */}
            <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
              {["market", "limit", "stop"].map(t => (
                <button key={t} onClick={() => setOrderType(t)} style={{
                  flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em",
                  background: orderType === t ? `${s.color}22` : "rgba(255,255,255,0.03)",
                  border: `1px solid ${orderType === t ? s.color + "55" : "rgba(255,255,255,0.06)"}`,
                  color: orderType === t ? s.color : "#444",
                  transition: "all 0.15s",
                }}>{t}</button>
              ))}
            </div>

            {/* Quantity */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontWeight: 700 }}>Shares</div>
              <div style={{ position: "relative" }}>
                <input
                  value={qty}
                  onChange={e => setQty(e.target.value)}
                  type="number"
                  style={{
                    width: "100%", background: "rgba(255,255,255,0.04)", border: `1px solid ${s.color}33`,
                    borderRadius: 12, padding: "14px 18px", color: "#fff", fontSize: 22, fontWeight: 900,
                    outline: "none", boxSizing: "border-box",
                    boxShadow: `0 0 0 0 ${s.color}33`,
                    transition: "box-shadow 0.2s",
                  }}
                  onFocus={e => e.target.style.boxShadow = `0 0 0 3px ${s.color}22`}
                  onBlur={e => e.target.style.boxShadow = "none"}
                />
              </div>
            </div>

            {/* Summary */}
            <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "16px 18px", marginBottom: 20 }}>
              {[
                { label: "Price per share", value: `$${s.price.toFixed(2)}` },
                { label: "Quantity", value: qty || "0" },
                { label: "Est. Total", value: `$${total.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, large: true, accent: s.color },
              ].map(({ label, value, large, accent }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>{label}</span>
                  <span style={{ fontSize: large ? 18 : 13, fontWeight: large ? 900 : 600, color: accent || "#ccc" }}>{value}</span>
                </div>
              ))}
            </div>

            {/* Powered by */}
            <div style={{ display: "flex", justifyContent: "center", gap: 16, marginBottom: 16 }}>
              {["⚡ Alpaca Securities", "🏦 Plaid Funded"].map(label => (
                <span key={label} style={{ fontSize: 10, color: "#94a3b8", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 6, padding: "4px 10px" }}>{label}</span>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={handleSubmit}
              style={{
                width: "100%", padding: "15px", borderRadius: 14, border: "none", cursor: "pointer",
                background: side === "buy"
                  ? "linear-gradient(135deg, #00ffaa, #00cc88)"
                  : "linear-gradient(135deg, #ff4466, #cc2244)",
                color: "#000", fontSize: 14, fontWeight: 900, letterSpacing: "0.06em",
                boxShadow: side === "buy" ? "0 8px 32px rgba(0,255,170,0.4)" : "0 8px 32px rgba(255,68,102,0.4)",
                transition: "all 0.2s",
                textTransform: "uppercase",
              }}
            >{side === "buy" ? `▲ Buy ${qty || 0} ${s.ticker}` : `▼ Sell ${qty || 0} ${s.ticker}`}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sector pill ───────────────────────────────────────────────────────────────
const SECTOR_COLORS = {
  "ALL": "#ffffff",
  "Franchise": "#00e5ff",
  "Sports Betting": "#53d337",
  "Sportswear": "#ff6600",
  "Sports Media": "#aa44ff",
  "Venues": "#ffcc00",
  "Esports": "#7c3aed",
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function SportsPublicMarket() {
  const { w } = useWindowSize();
  const isMobile = w < 768;
  const [sector, setSector] = useState("ALL");
  const [watchlist, setWatchlist] = useState(["MSGS", "DKNG", "NKE"]);
  const [tradeTarget, setTradeTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [view, setView] = useState("grid"); // grid | list
  const [sparkData] = useState(() => Object.fromEntries(SECURITIES.map(s => [s.ticker, generateSparkData(s.change >= 0, Math.abs(s.change) > 2)])));
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => { setHeaderVisible(true); }, []);

  const filtered = sector === "ALL" ? SECURITIES : SECURITIES.filter(s => s.sector === sector);
  const toggleWatch = (t) => setWatchlist(w => w.includes(t) ? w.filter(x => x !== t) : [...w, t]);

  const gainers = [...SECURITIES].sort((a, b) => b.change - a.change).slice(0, 3);
  const losers = [...SECURITIES].sort((a, b) => a.change - b.change).slice(0, 3);

  return (
    <div style={{ background: "transparent", minHeight: "100vh", fontFamily: "system-ui,-apple-system,sans-serif", color: "#fff", position: "relative", overflow: "hidden" }}>

      {/* Background grid */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none", zIndex: 0 }} />

      {/* Ambient glow */}
      <div style={{ position: "fixed", top: "20%", left: "10%", width: 600, height: 600, background: "radial-gradient(circle, rgba(0,212,170,0.04) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />
      <div style={{ position: "fixed", top: "50%", right: "5%", width: 400, height: 400, background: "radial-gradient(circle, rgba(124,111,255,0.04) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0 }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Ticker tape */}
        <TickerTape securities={SECURITIES} />

        {/* Header */}
        <div style={{ padding: isMobile ? "12px 12px 0" : "32px 28px 0", maxWidth: 1400, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32, opacity: headerVisible ? 1 : 0, transform: headerVisible ? "none" : "translateY(-20px)", transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: "#00ffaa", fontFamily: "system-ui,-apple-system,sans-serif", fontWeight: 700, background: "rgba(0,255,170,0.08)", border: "1px solid rgba(0,255,170,0.2)", padding: "4px 12px", borderRadius: 20, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                  ● Live Market
                </div>
                <div style={{ fontSize: 10, color: "#64748b", fontFamily: "system-ui,-apple-system,sans-serif" }}>NYSE · NASDAQ · OTC</div>
              </div>
              <h1 style={{ fontSize: "clamp(36px, 5vw, 64px)", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 0.95, margin: 0, textTransform: "uppercase" }}>
                Sports<br />
                <span style={{ WebkitTextStroke: "1px rgba(255,255,255,0.3)", color: "transparent" }}>Public</span>{" "}
                <span style={{ background: "linear-gradient(135deg, #00ffaa, #00ccff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Market</span>
              </h1>
              <p style={{ fontFamily: "system-ui,-apple-system,sans-serif", fontSize: 14, color: "#64748b", marginTop: 12, fontWeight: 400 }}>
                16 sports securities · Trade via Alpaca · Fund via Plaid
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {["grid", "list"].map(v => (
                <button key={v} onClick={() => setView(v)} style={{
                  padding: "8px 16px", borderRadius: 10, border: `1px solid ${view === v ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.06)"}`,
                  background: view === v ? "rgba(255,255,255,0.08)" : "transparent",
                  color: view === v ? "#fff" : "#444", fontSize: 11, cursor: "pointer", fontFamily: "system-ui,-apple-system,sans-serif", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
                }}>{v === "grid" ? "⊞ Grid" : "≡ List"}</button>
              ))}
            </div>
          </div>

          {/* Hero stats */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 28 }}>
            <HeroStat label="Securities Tracked" value="16" sub="Across 6 sectors" accent="#00ffaa" icon="📊" delay={100} />
            <HeroStat label="Tradeable Now" value="16" sub="Via Alpaca Securities" accent="#00ccff" icon="⚡" delay={200} />
            <HeroStat label="Watchlisted" value={watchlist.length} sub="Your saved picks" accent="#ffd700" icon="★" delay={300} />
            <HeroStat label="Market Pulse" value="+1.2%" sub="Avg sector today" accent="#00ffaa" icon="📈" delay={400} />
          </div>

          {/* Gainers / Losers strip */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 28 }}>
            {[
              { label: "🔥 Top Gainers", items: gainers, positive: true },
              { label: "📉 Top Losers", items: losers, positive: false },
            ].map(({ label, items, positive }) => (
              <div key={label} style={{ background: "rgba(255,255,255,0.02)", border: `1px solid ${positive ? "rgba(0,255,170,0.12)" : "rgba(255,68,102,0.12)"}`, borderRadius: 14, padding: "14px 18px" }}>
                <div style={{ fontSize: 11, fontFamily: "system-ui,-apple-system,sans-serif", color: positive ? "#00ffaa" : "#ff4466", fontWeight: 700, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>{label}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {items.map(s => (
                    <div key={s.ticker} onClick={() => setDetailTarget(s)} style={{ flex: 1, background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: "10px 12px", cursor: "pointer", border: `1px solid ${s.color}22`, transition: "all 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background = `${s.color}12`}
                      onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                    >
                      <div style={{ fontSize: 16, marginBottom: 4 }}>{s.emoji}</div>
                      <div style={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>{s.ticker}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: positive ? "#00ffaa" : "#ff4466" }}>{positive ? "+" : ""}{s.change.toFixed(2)}%</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Sector filters */}
          <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
            {SECTORS.map(s => {
              const col = SECTOR_COLORS[s] || "#fff";
              const active = sector === s;
              return (
                <button key={s} onClick={() => setSector(s)} style={{
                  padding: "8px 18px", borderRadius: 24, border: `1px solid ${active ? col + "66" : "rgba(255,255,255,0.08)"}`,
                  background: active ? `${col}18` : "rgba(255,255,255,0.03)",
                  color: active ? col : "#555",
                  fontSize: 11, fontWeight: 800, cursor: "pointer",
                  fontFamily: "system-ui,-apple-system,sans-serif", letterSpacing: "0.06em", textTransform: "uppercase",
                  transition: "all 0.2s",
                  boxShadow: active ? `0 0 20px ${col}22` : "none",
                }}>{s}</button>
              );
            })}
          </div>

          {/* Securities grid */}
          {view === "grid" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14, paddingBottom: 40 }}>
              {filtered.map((s, i) => (
                <SecurityCard key={s.ticker} s={s} onTrade={setTradeTarget} onDetail={setDetailTarget} inWatchlist={watchlist.includes(s.ticker)} onWatch={toggleWatch} delay={i * 60} sparkData={sparkData[s.ticker]} />
              ))}
            </div>
          )}

          {/* List view */}
          {view === "list" && (
            <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, overflow: "hidden", marginBottom: 40 }}>
              <div style={{ display: "grid", gridTemplateColumns: "60px 60px 1fr 120px 120px 90px 90px 80px 130px", gap: 8, padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                {["", "Ticker", "Name", "Price", "Change", "Mkt Cap", "Volume", "P/E", ""].map((h, i) => (
                  <div key={i} style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 800, fontFamily: "system-ui,-apple-system,sans-serif" }}>{h}</div>
                ))}
              </div>
              {filtered.map((s, i) => {
                const pos = s.change >= 0;
                return (
                  <div key={s.ticker} style={{ display: "grid", gridTemplateColumns: "60px 60px 1fr 120px 120px 90px 90px 80px 130px", gap: 8, padding: "13px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)", alignItems: "center", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = `${s.color}08`}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ fontSize: 22 }}>{s.emoji}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: s.color }}>{s.ticker}</div>
                      <div style={{ fontSize: 9, color: "#94a3b8", fontFamily: "system-ui,-apple-system,sans-serif" }}>{s.exchange}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#cbd5e1", fontFamily: "system-ui,-apple-system,sans-serif" }}>{s.name}</div>
                      <div style={{ fontSize: 9, color: "#94a3b8", fontFamily: "system-ui,-apple-system,sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.sector}</div>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: "#fff" }}>${s.price.toFixed(2)}</div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: pos ? "#00ffaa" : "#ff4466" }}>{pos ? "▲" : "▼"} {Math.abs(s.change).toFixed(2)}%</div>
                      <div style={{ fontSize: 10, color: pos ? "#00ffaa66" : "#ff446666", fontFamily: "system-ui,-apple-system,sans-serif" }}>{pos ? "+" : ""}{s.changeAmt.toFixed(2)}</div>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", fontFamily: "system-ui,-apple-system,sans-serif" }}>{s.mktCap}</div>
                    <div style={{ fontSize: 12, color: "#64748b", fontFamily: "system-ui,-apple-system,sans-serif" }}>{s.vol}</div>
                    <div style={{ fontSize: 12, color: "#64748b", fontFamily: "system-ui,-apple-system,sans-serif" }}>{s.pe ? `${s.pe}x` : "—"}</div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => toggleWatch(s.ticker)} style={{ background: "none", border: "none", fontSize: 14, cursor: "pointer", color: watchlist.includes(s.ticker) ? "#ffd700" : "#333", transition: "all 0.15s" }}>{watchlist.includes(s.ticker) ? "★" : "☆"}</button>
                      <button onClick={() => setDetailTarget(s)} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#94a3b8", fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "system-ui,-apple-system,sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", transition: "all 0.15s", display:"flex", alignItems:"center", gap:"4px" }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${s.color}22`; e.currentTarget.style.borderColor = `${s.color}55`; e.currentTarget.style.color = s.color; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "#94a3b8"; }}
                      >🔬 Analyze</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Watchlist */}
          {watchlist.length > 0 && (
            <div style={{ marginBottom: 40, background: "rgba(255,215,0,0.03)", border: "1px solid rgba(255,215,0,0.12)", borderRadius: 16, padding: "18px 20px" }}>
              <div style={{ fontSize: 11, fontFamily: "system-ui,-apple-system,sans-serif", color: "#ffd700", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>★ Watchlist — {watchlist.length} picks</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {watchlist.map(t => {
                  const s = SECURITIES.find(x => x.ticker === t);
                  if (!s) return null;
                  const pos = s.change >= 0;
                  return (
                    <div key={t} onClick={() => setDetailTarget(s)} style={{ background: `${s.color}10`, border: `1px solid ${s.color}33`, borderRadius: 12, padding: "12px 16px", cursor: "pointer", minWidth: 110, transition: "all 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${s.color}22`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${s.color}10`; e.currentTarget.style.transform = "none"; }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 18 }}>{s.emoji}</span>
                        <button onClick={e => { e.stopPropagation(); toggleWatch(t); }} style={{ background: "none", border: "none", color: "#ffd700", cursor: "pointer", fontSize: 12 }}>★</button>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: s.color }}>{s.ticker}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>${s.price.toFixed(2)}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: pos ? "#00ffaa" : "#ff4466", fontFamily: "system-ui,-apple-system,sans-serif" }}>{pos ? "+" : ""}{s.change.toFixed(2)}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {detailTarget && <StockDetailPanel s={detailTarget} onClose={() => setDetailTarget(null)} onTrade={s => { setDetailTarget(null); setTradeTarget(s); }} />}
      {/* TradeModal removed — trading done via Live Trading section */}

      <style>{`
        /* system fonts */
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.94) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes popIn { from { transform: scale(0); } to { transform: scale(1); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
        input[type=number]::-webkit-inner-spin-button { opacity: 0.3; }
      `}</style>
    </div>
  );
}